import { COUNTRY_CODES, ValidatingInput, ValidationForm, ValidationSummary } from '@ygtechnologies/bygr-frontend-component-library';
import theme from './AddProfileModal.module.scss';
import React, { useEffect, useState } from 'react';
import {
    addProfile,
    addProfileOptions,
    checkProfileName,
    createChildProfile,
    getProfiles,
    setProfileParent,
} from '@/services/profiles';
import { useAsyncState } from '@/utils/hooks';
import { getCurrencies } from '@/services/currencies';
import { BygrProfile } from '@/types';
import { AlertBanner, Button, ButtonBar, Checkbox, Dialog } from '@ygtechnologies/rubus-ui';

interface AddProfileModalProps {
    isOpen: boolean;
    toggle(): void;
    onSave(): void;
    parentProfileId?: number;
    parentProfileName?: string;
    excludeProfileIds?: number[];
}

type Mode = 'create' | 'adopt';

const AddProfileModal: React.FC<AddProfileModalProps> = ({
    isOpen,
    toggle,
    onSave,
    parentProfileId,
    parentProfileName,
    excludeProfileIds = [],
}) => {
    const canAdopt = parentProfileId != null;
    const [mode, setMode] = useState<Mode>('create');
    const [profileOptions, setProfileOptions] = useState<addProfileOptions>({
        isPaying: false,
        name: '',
        currencyId: 1,
        countryCode: 'NO',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [skipNameValidation, setSkipNameValidation] = useState(false);
    const [similarProfileNames, setSimilarProfileNames] = useState<string[]>([]);
    const [currencies] = useAsyncState(() => getCurrencies(), [], null);

    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<BygrProfile[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [adoptError, setAdoptError] = useState<string | null>(null);
    // Keyed by id so ticks survive changing the search text — you can search
    // "Tell", tick two, search "Borg", tick one more, then add all three.
    const [selected, setSelected] = useState<Map<number, BygrProfile>>(new Map());
    const [addedNotice, setAddedNotice] = useState<string | null>(null);

    useEffect(() => {
        if (mode !== 'adopt') return;
        if (searchText.trim().length === 0) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        const handle = setTimeout(async () => {
            try {
                const res = await getProfiles({
                    pageNumber: 0,
                    pageSize: 10,
                    sortBy: 'name',
                    sortDirection: 'Ascending' as any,
                    searchText,
                });
                setSearchResults(res.items);
            } finally {
                setIsSearching(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [searchText, mode]);

    useEffect(() => {
        if (!isOpen) {
            setMode('create');
            setSearchText('');
            setSearchResults([]);
            setAdoptError(null);
            setSelected(new Map());
            setAddedNotice(null);
        }
    }, [isOpen]);

    const handleAddProfile = async () => {
        setIsSaving(true);
        try {
            // Skip fuzzy name check when creating a child profile — sub-orgs are
            // expected to share name patterns with their parent/siblings.
            if (!canAdopt && !skipNameValidation) {
                const res = await checkProfileName(profileOptions.name);
                if (res.length) {
                    setSimilarProfileNames(res);
                    setSkipNameValidation(true);
                    setIsSaving(false);
                    return;
                }
            }
            if (canAdopt) {
                await createChildProfile(parentProfileId!, profileOptions);
            } else {
                await addProfile(profileOptions);
            }
            onSave();
            toggle();
        } finally {
            setIsSaving(false);
        }
    };

    const toggleSelected = (p: BygrProfile) =>
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(p.id)) next.delete(p.id);
            else next.set(p.id, p);
            return next;
        });

    // `request` rejects with the raw Response; try to read the JSON body's error field.
    const readError = async (e: any): Promise<string> => {
        if (e && typeof e.json === 'function') {
            try {
                const body = await e.json();
                if (body?.error) return body.error;
            } catch {
                // response body wasn't JSON — fall through
            }
        }
        return e?.message || 'Failed to set parent.';
    };

    const handleAdoptSelected = async () => {
        setAdoptError(null);
        setAddedNotice(null);
        setIsSaving(true);

        // Sequential, not Promise.all: each re-parent reconciles inherited
        // membership across the tree, and firing them concurrently against the
        // same parent interleaves those reconciles.
        // ponytail: N is a handful; revisit if anyone adopts dozens at once.
        const picked = [...selected.values()];
        const failed: BygrProfile[] = [];
        const messages: string[] = [];
        for (const p of picked) {
            try {
                await setProfileParent(p.id, parentProfileId!);
            } catch (e) {
                failed.push(p);
                messages.push(`${p.name}: ${await readError(e)}`);
            }
        }
        setIsSaving(false);

        const addedCount = picked.length - failed.length;
        // Keep the ones that failed ticked so they can be retried; drop the rest.
        setSelected(new Map(failed.map((p) => [p.id, p])));
        if (addedCount > 0) {
            onSave();
            setAddedNotice(`Added ${addedCount} child profile${addedCount === 1 ? '' : 's'}.`);
        }
        if (messages.length) setAdoptError(messages.join('\n'));
    };

    const handleChange = (changes: Partial<addProfileOptions>, resetNameCheck: boolean = false) => {
        if (resetNameCheck) {
            setSkipNameValidation(false);
        }
        setProfileOptions((prevValue) => ({ ...prevValue, ...changes }));
    };

    const excludeSet = new Set<number>([
        ...excludeProfileIds,
        ...(parentProfileId != null ? [parentProfileId] : []),
    ]);
    const filteredResults = searchResults.filter((p) => !excludeSet.has(p.id));

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()}>
            <Dialog.Content contentWidth="sm">
                <Dialog.Header>
                    {parentProfileName ? `Add child profile under ${parentProfileName}` : 'Add profile'}
                </Dialog.Header>
                {canAdopt && (
                    <div className={theme.modeTabs}>
                        <ModeTab active={mode === 'create'} onClick={() => setMode('create')}>
                            Create new
                        </ModeTab>
                        <ModeTab active={mode === 'adopt'} onClick={() => setMode('adopt')}>
                            Add existing
                        </ModeTab>
                    </div>
                )}

                {mode === 'create' ? (
                    <ValidationForm onSubmit={handleAddProfile}>
                        <div className={theme.body}>
                            <div className="form columns-1">
                                <label>
                                    Name
                                    <ValidatingInput
                                        value={profileOptions.name}
                                        required
                                        fieldName="Name"
                                        placeholder="Name"
                                        className="form-control"
                                        onChange={(e) => handleChange({ name: e.target.value }, true)}
                                    />
                                </label>
                                <label>
                                    Country
                                    <select
                                        className="form-control"
                                        value={profileOptions.countryCode}
                                        onChange={(e) =>
                                            handleChange({
                                                countryCode: e.target.value as keyof typeof COUNTRY_CODES,
                                            })
                                        }>
                                        {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {currencies && (
                                    <label>
                                        Currency
                                        <select
                                            className="form-control"
                                            value={profileOptions.currencyId}
                                            onChange={(e) => handleChange({ currencyId: +e.target.value })}>
                                            {currencies.map((x) => (
                                                <option key={x.id} value={x.id}>
                                                    {x.name} ({x.code})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <Checkbox
                                    inputSize="md"
                                    checked={profileOptions.isPaying}
                                    onChange={(e) => handleChange({ isPaying: e.target.checked })}>
                                    Is Paying
                                </Checkbox>
                                {similarProfileNames.length > 0 && (
                                    <>
                                        <AlertBanner type="warning">
                                            There are Profiles with similar names as the one you want to create. If you still want to
                                            create the profile with this name, save again.
                                        </AlertBanner>
                                        <AlertBanner type="info">
                                            Profiles with similar names:
                                            <ul>
                                                {similarProfileNames.map((x) => (
                                                    <li>{x}</li>
                                                ))}
                                            </ul>
                                        </AlertBanner>
                                    </>
                                )}
                            </div>
                            <ValidationSummary />
                        </div>
                        <ButtonBar>
                            <ButtonBar.Right>
                                <Button onClick={toggle} variant="tertiary">
                                    Cancel
                                </Button>
                                <Button loading={isSaving} type="submit" variant="primary">
                                    {canAdopt ? 'Add child profile' : 'Add profile'}
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </ValidationForm>
                ) : (
                    <>
                        <div className={theme.body}>
                            <label>
                                Search for an existing profile
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Type a profile name..."
                                    autoFocus
                                />
                            </label>
                            <div className={theme.adoptResults}>
                                {searchText.trim().length === 0 ? (
                                    <p className={theme.adoptHint}>Start typing to search.</p>
                                ) : isSearching ? (
                                    <p className={theme.adoptHint}>Searching...</p>
                                ) : filteredResults.length === 0 ? (
                                    <p className={theme.adoptHint}>No matching profiles.</p>
                                ) : (
                                    <div className={theme.adoptList}>
                                        {filteredResults.map((p) => (
                                            <div key={p.id} className={theme.adoptRow}>
                                                <Checkbox
                                                    checked={selected.has(p.id)}
                                                    onChange={() => toggleSelected(p)}>
                                                    <div>{p.name}</div>
                                                    <div className={theme.adoptRowMeta}>
                                                        {p.numberOfMembers} member
                                                        {p.numberOfMembers === 1 ? '' : 's'} · Paying:{' '}
                                                        {p.isPaying ? 'Yes' : 'No'}
                                                    </div>
                                                </Checkbox>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {selected.size > 0 && (
                                <div className={theme.adoptSelected}>
                                    Selected: {[...selected.values()].map((p) => p.name).join(', ')}
                                </div>
                            )}
                            {addedNotice && (
                                <div className={theme.adoptError}>
                                    <AlertBanner type="success">{addedNotice}</AlertBanner>
                                </div>
                            )}
                            {adoptError && (
                                <div className={theme.adoptError}>
                                    <AlertBanner type="warning">{adoptError}</AlertBanner>
                                </div>
                            )}
                        </div>
                        <ButtonBar>
                            <ButtonBar.Right>
                                <Button onClick={toggle} variant="tertiary">
                                    Close
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={isSaving}
                                    disabled={selected.size === 0}
                                    onClick={handleAdoptSelected}>
                                    {selected.size > 1 ? `Add ${selected.size} as children` : 'Add as child'}
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </>
                )}
            </Dialog.Content>
        </Dialog>
    );
};

interface ModeTabProps {
    active: boolean;
    onClick(): void;
    children: React.ReactNode;
}

const ModeTab: React.FC<ModeTabProps> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={active ? `${theme.modeTab} ${theme.modeTabActive}` : theme.modeTab}>
        {children}
    </button>
);

export default AddProfileModal;
